import React, { useState, useCallback, useMemo, useEffect } from "react";
import "./TeamDetails.scss";
import { Icon, TypeBadge, ConfirmModal, FormDrawer } from "./sharedComponents";

const ROLE_OPTIONS = ["Owner", "Admin", "Member", "Guest"];
const POST_OPTIONS = ["All Members", "Owner/Admin Only", "Selected Members"];
const CHANNEL_TYPES = ["Standard", "Private", "Announcement", "Project"];

function StatusBadge({ status }) {
  const isActive = status === "Active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "999px",
        textTransform: "capitalize",
        background: isActive
          ? "rgba(34, 197, 94, 0.1)"
          : "rgba(239, 68, 68, 0.1)",
        color: isActive ? "#16a34a" : "#ef4444",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

function TeamDetails({
  team,
  departments,
  users,
  onBack,
  onUpdateTeam,
  onDeleteTeam,
  onAddMember,
  onUpdateMemberRole,
  onRemoveMember,
  onCreateChannel,
  onArchiveChannel,
  onGetChannelMembers,
  onAddChannelMember,
  fetchTeamDetails,
  onRemoveChannelMember,
  onGetTeamAuditLogs,
}) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [showAddMember, setShowAddMember] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [showChannelMembers, setShowChannelMembers] = useState(false);
  const [showAddChannelMember, setShowAddChannelMember] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelMembers, setChannelMembers] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [permissions, setPermissions] = useState(team?.permissions || {});
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);

  useEffect(() => {
    if (team) {
      setPermissions(team.permissions || {});
    }
  }, [team]);

  useEffect(() => {
    if (activeTab === "Audit" && onGetTeamAuditLogs && team) {
      loadAuditLogs(1);
    }
  }, [activeTab, team]);

  const loadAuditLogs = async (page = 1) => {
    if (!onGetTeamAuditLogs) return;
    setAuditLoading(true);
    const result = await onGetTeamAuditLogs(team.id, page);
    if (result) {
      setAuditLogs(result.logs || []);
      setAuditTotal(result.pagination?.total || 0);
      setAuditPage(page);
    }
    setAuditLoading(false);
  };

  const tabs = [
    "Overview",
    "Members",
    "Channels",
    "Permissions",
    "Files",
    "Meetings",
    "Audit",
  ];

  const handleEditTeam = useCallback(
    (form) => {
      onUpdateTeam({
        ...team,
        name: form.name,
        description: form.description,
        departmentId: form.departmentId,
        type: form.privacyType,
        ownerId: form.ownerId,
      });
      setShowEditTeam(false);
    },
    [team, onUpdateTeam],
  );

  const handleAddMember = useCallback(
    (form) => {
      onAddMember(team.id, form);
      setShowAddMember(false);
    },
    [team, onAddMember],
  );

  const handleCreateChannel = useCallback(
    (form) => {
      onCreateChannel(team.id, form);
      setShowCreateChannel(false);
    },
    [team, onCreateChannel],
  );

  const savePermissions = useCallback(() => {
    onUpdateTeam({ ...team, permissions });
  }, [team, permissions, onUpdateTeam]);

  const toggleTeamStatus = useCallback(() => {
    const newStatus = team.status === "Active" ? "Disabled" : "Active";
    setConfirm({
      title: newStatus === "Disabled" ? "Disable team" : "Enable team",
      message:
        newStatus === "Disabled"
          ? `Disable ${team.name}? Members will no longer be able to post or join channels.`
          : `Re-enable ${team.name}? Members will regain access.`,
      confirmLabel: newStatus === "Disabled" ? "Disable" : "Enable",
      danger: newStatus === "Disabled",
      onConfirm: () => {
        onUpdateTeam({ ...team, status: newStatus });
        setConfirm(null);
      },
    });
  }, [team, onUpdateTeam]);

  const handleOpenChannelMembers = useCallback(
    async (channel) => {
      setSelectedChannel(channel);
      const members = await onGetChannelMembers(channel.id);
      setChannelMembers(members);
      setShowChannelMembers(true);
    },
    [onGetChannelMembers],
  );

  const handleAddChannelMemberSubmit = useCallback(
    async (form) => {
      const success = await onAddChannelMember(selectedChannel.id, form.userId);
      if (success) {
        await fetchTeamDetails(team.id);
        const members = await onGetChannelMembers(selectedChannel.id);
        setChannelMembers(members);
        setShowAddChannelMember(false);
        setShowChannelMembers(true);
      }
    },
    [
      team.id,
      selectedChannel,
      fetchTeamDetails,
      onAddChannelMember,
      onGetChannelMembers,
    ],
  );

  const handleRemoveChannelMember = useCallback(
    async (userId) => {
      setConfirm({
        title: "Remove from channel",
        message: "Remove this user from the channel?",
        confirmLabel: "Remove",
        danger: true,
        onConfirm: async () => {
          const success = await onRemoveChannelMember(
            selectedChannel.id,
            userId,
          );
          if (success) {
            const members = await onGetChannelMembers(selectedChannel.id);
            setChannelMembers(members);
            await fetchTeamDetails(team.id);
          }
          setConfirm(null);
        },
      });
    },
    [
      selectedChannel,
      onRemoveChannelMember,
      onGetChannelMembers,
      team.id,
      fetchTeamDetails,
    ],
  );

  const addMemberFields = useMemo(() => {
    return [
      {
        name: "userId",
        label: "User",
        type: "select",
        required: true,
        placeholder: "Select user",
        options: users
          .filter((u) => !team.members?.find((m) => m.id === u.id))
          .map((u) => ({
            value: u.id,
            label: `${u.fullName} (${u.department?.name || "N/A"})`,
          })),
      },
      {
        name: "role",
        label: "Team Role",
        type: "select",
        options: ROLE_OPTIONS.map((r) => ({ value: r, label: r })),
        defaultValue: "Member",
      },
    ];
  }, [users, team]);

  const createChannelFields = useMemo(
    () => [
      {
        name: "name",
        label: "Channel Name",
        type: "text",
        placeholder: "e.g. Project Alpha",
        required: true,
      },
      {
        name: "type",
        label: "Channel Type",
        type: "select",
        options: CHANNEL_TYPES.map((t) => ({ value: t, label: t })),
        defaultValue: "Standard",
      },
      {
        name: "whoCanPost",
        label: "Who Can Post",
        type: "select",
        options: POST_OPTIONS.map((p) => ({ value: p, label: p })),
        defaultValue: "All Members",
      },
    ],
    [],
  );

  const addChannelMemberFields = useMemo(() => {
    if (!selectedChannel || !team.members) return [];
    return [
      {
        name: "userId",
        label: "Team Member",
        type: "select",
        required: true,
        placeholder: "Select team member to add",
        options: team.members
          .filter((m) => !channelMembers.find((cm) => cm.id === m.id))
          .map((u) => ({
            value: u.id,
            label: `${u.name || u.fullName} (${u.department})`,
          })),
      },
    ];
  }, [selectedChannel, team.members, channelMembers]);

  const editTeamFields = useMemo(() => {
    if (!team) return [];
    return [
      { name: "name", label: "Team Name", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea", rows: 3 },
      {
        name: "row1",
        type: "custom",
        render: ({ form, updateField }) => (
          <div className="form-row">
            <div className="form-group">
              <label>Linked Department</label>
              <select
                value={form.departmentId || team.departmentId || ""}
                onChange={(e) => updateField("departmentId", e.target.value)}
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Privacy Type</label>
              <select
                value={form.privacyType || team.type}
                onChange={(e) => updateField("privacyType", e.target.value)}
              >
                <option value="Standard">Standard</option>
                <option value="Private">Private</option>
              </select>
            </div>
          </div>
        ),
      },
      {
        name: "ownerId",
        label: "Owner",
        type: "select",
        required: true,
        options: (team.members || []).map((m) => ({
          value: m.id,
          label: `${m.name || m.fullName} (${m.department})`,
        })),
      },
    ];
  }, [team, departments]);

  if (!team) {
    return (
      <div className="team-details-page">
        <div className="empty-state">
          <Icon name="users" size={28} />
          <p>No team selected.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="team-details-page">
        <div className="team-details-page__breadcrumb">
          <button className="link-btn" onClick={onBack}>
            <Icon name="chevronLeft" size={14} /> Teams / Groups
          </button>
          <span className="team-details-page__sep">/</span>
          <span>{team.name}</span>
        </div>

        <div className="team-details-page__header">
          <div>
            <h2>{team.name}</h2>
            <div className="team-details-page__subtitle">
              <TypeBadge type={team.type} />
              <StatusBadge status={team.status} />
            </div>
          </div>
          <div className="team-details-page__header-actions">
            <button
              className="btn btn--secondary"
              onClick={() => setShowAddMember(true)}
            >
              <Icon name="users" size={14} /> Add Members
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => setShowCreateChannel(true)}
            >
              <Icon name="plus" size={14} /> Channel
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => setShowEditTeam(true)}
            >
              <Icon name="edit" size={14} /> Edit Team
            </button>
          </div>
        </div>

        <div className="tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`tabs__tab ${activeTab === tab ? "tabs__tab--active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="team-details-page__body">
          {activeTab === "Overview" && (
            <div className="overview-grid">
              <div className="overview-item">
                <span className="overview-item__label">Team Type</span>
                <span className="overview-item__value">
                  <TypeBadge type={team.type} />
                </span>
              </div>
              <div className="overview-item">
                <span className="overview-item__label">Linked Department</span>
                <span className="overview-item__value">{team.department}</span>
              </div>
              <div className="overview-item">
                <span className="overview-item__label">Owner</span>
                <span className="overview-item__value">{team.owner}</span>
              </div>
              <div className="overview-item">
                <span className="overview-item__label">Members</span>
                <span className="overview-item__value">
                  {team.members?.length || 0}
                </span>
              </div>
              <div className="overview-item overview-item--full">
                <span className="overview-item__label">Description</span>
                <span className="overview-item__value">
                  {team.description || "—"}
                </span>
              </div>
              <div className="overview-item overview-item--full">
                <span className="overview-item__label">Channels</span>
                <div className="chip-list">
                  {(team.channels || []).map((c) => (
                    <span key={c.id} className="chip">
                      <Icon name="hash" size={12} />
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "Members" && (
            <div className="table-wrap table-wrap--card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Department</th>
                    <th>Team Role</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(team.members || []).map((m) => (
                    <tr key={m.id}>
                      <td className="cell-strong">{m.name || m.fullName}</td>
                      <td>{m.department}</td>
                      <td>
                        <select
                          className="inline-select"
                          value={m.role}
                          onChange={(e) =>
                            onUpdateMemberRole(team.id, m.id, e.target.value)
                          }
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <StatusBadge status={m.status || "Active"} />
                      </td>
                      <td>
                        {m.role !== "Owner" ? (
                          <button
                            className="link-btn link-btn--danger"
                            onClick={() =>
                              setConfirm({
                                title: "Remove member",
                                message: `Remove ${m.name || m.fullName}?`,
                                confirmLabel: "Remove",
                                danger: true,
                                onConfirm: () => {
                                  onRemoveMember(team.id, m.id);
                                  setConfirm(null);
                                },
                              })
                            }
                          >
                            Remove
                          </button>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                            Owner
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "Channels" && (
            <div className="table-wrap table-wrap--card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Channel Name</th>
                    <th>Type</th>
                    <th>Who Can Post</th>
                    <th>Members</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(team.channels || []).map((c) => (
                    <tr key={c.id}>
                      <td className="cell-strong">
                        <Icon name="hash" size={14} /> {c.name}
                      </td>
                      <td>
                        <TypeBadge type={c.type} />
                      </td>
                      <td>{c.whoCanPost}</td>
                      <td>
                        <button
                          className="link-btn"
                          onClick={() => handleOpenChannelMembers(c)}
                        >
                          {c.members || 0} members
                        </button>
                      </td>
                      <td>
                        <StatusBadge status={c.status} />
                      </td>
                      <td>
                        <button
                          className="link-btn"
                          onClick={() => onArchiveChannel(c.id, c.status)}
                        >
                          {c.status === "Active" ? "Archive" : "Unarchive"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "Permissions" && (
            <div className="permissions-form">
              {Object.entries(permissions).map(([key, value]) => (
                <div key={key} className="form-group">
                  <label>
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())}
                  </label>
                  <select
                    value={value}
                    onChange={(e) =>
                      setPermissions({ ...permissions, [key]: e.target.value })
                    }
                  >
                    {POST_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              <button className="btn btn--primary" onClick={savePermissions}>
                Save Permissions
              </button>
            </div>
          )}

          {activeTab === "Audit" && (
            <div className="table-wrap table-wrap--card">
              {auditLoading ? (
                <div className="empty-state">
                  <p>Loading audit logs...</p>
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="empty-state">
                  <Icon name="shield" size={28} />
                  <p>No audit logs found for this team.</p>
                </div>
              ) : (
                <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>User</th>
                        <th>Date & Time</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="cell-strong">{log.action}</td>
                          <td>{log.user}</td>
                          <td
                            style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                          >
                            {new Date(log.date).toLocaleString()}
                          </td>
                          <td style={{ fontSize: "12px", color: "#6b7280" }}>
                            {typeof log.details === "object"
                              ? JSON.stringify(log.details)
                              : log.details || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {auditTotal > 20 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                        padding: "16px",
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      <button
                        className="btn btn--secondary btn--sm"
                        disabled={auditPage <= 1}
                        onClick={() => loadAuditLogs(auditPage - 1)}
                      >
                        Previous
                      </button>
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                          padding: "6px",
                        }}
                      >
                        Page {auditPage} of {Math.ceil(auditTotal / 20)}
                      </span>
                      <button
                        className="btn btn--secondary btn--sm"
                        disabled={auditPage >= Math.ceil(auditTotal / 20)}
                        onClick={() => loadAuditLogs(auditPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {(activeTab === "Files" || activeTab === "Meetings") && (
            <div className="empty-state">
              <Icon
                name={activeTab === "Files" ? "file" : "calendar"}
                size={28}
              />
              <p>{activeTab} data will be available soon.</p>
            </div>
          )}
        </div>

        <div className="team-details-page__footer">
          <button
            className="link-btn link-btn--danger"
            onClick={toggleTeamStatus}
          >
            <Icon name="power" size={14} />{" "}
            {team.status === "Active" ? "Disable Team" : "Enable Team"}
          </button>
          <button
            className="link-btn link-btn--danger"
            onClick={() =>
              setConfirm({
                title: "Delete team",
                message: `Are you sure you want to delete ${team.name}? This action cannot be undone.`,
                confirmLabel: "Delete",
                danger: true,
                onConfirm: () => {
                  onDeleteTeam(team.id);
                  setConfirm(null);
                },
              })
            }
          >
            <Icon name="trash" size={14} /> Delete Team
          </button>
          <button className="btn btn--secondary" onClick={onBack}>
            Back to Teams / Groups
          </button>
        </div>

        {showAddMember && (
          <FormDrawer
            open={showAddMember}
            title="Add Member"
            fields={addMemberFields}
            onClose={() => setShowAddMember(false)}
            submitLabel="Add Member"
            onSubmit={handleAddMember}
          />
        )}
        {showCreateChannel && (
          <FormDrawer
            open={showCreateChannel}
            title="Create Channel"
            fields={createChannelFields}
            onClose={() => setShowCreateChannel(false)}
            submitLabel="Create Channel"
            onSubmit={handleCreateChannel}
          />
        )}
        {showEditTeam && (
          <FormDrawer
            open={showEditTeam}
            editMode={true}
            initialData={{
              name: team.name,
              description: team.description || "",
              departmentId: team.departmentId,
              privacyType: team.type,
              ownerId: team.ownerId,
            }}
            title="Edit Team"
            fields={editTeamFields}
            onClose={() => setShowEditTeam(false)}
            submitLabel="Save Changes"
            onSubmit={handleEditTeam}
          />
        )}

        {showChannelMembers && selectedChannel && (
          <div
            className="td-chm-overlay"
            onClick={() => setShowChannelMembers(false)}
          >
            <div className="td-chm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="td-chm-header">
                <div>
                  <h3>
                    <Icon name="hash" size={18} />
                    {selectedChannel.name}
                  </h3>
                  <p>
                    {channelMembers.length} member
                    {channelMembers.length !== 1 ? "s" : ""} in this channel
                  </p>
                </div>
                <button
                  className="icon-btn"
                  onClick={() => setShowChannelMembers(false)}
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="td-chm-body">
                {channelMembers.length === 0 ? (
                  <div className="td-chm-empty">
                    <Icon name="users" size={48} />
                    <p>
                      <strong
                        style={{
                          color: "#374151",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        No members yet
                      </strong>
                      Add team members to this channel to start collaborating
                    </p>
                    <button
                      className="btn btn--primary"
                      onClick={() => {
                        setShowAddChannelMember(true);
                        setShowChannelMembers(false);
                      }}
                    >
                      Add Member
                    </button>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Department</th>
                        <th>Role</th>
                        <th style={{ width: "80px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {channelMembers.map((m) => (
                        <tr key={m.id}>
                          <td className="cell-strong">{m.name}</td>
                          <td>{m.department}</td>
                          <td>
                            <span
                              style={{
                                display: "inline-block",
                                fontSize: "11px",
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: "4px",
                                background:
                                  m.role === "OWNER"
                                    ? "rgba(0,98,254,0.1)"
                                    : "rgba(107,114,128,0.1)",
                                color:
                                  m.role === "OWNER" ? "#0062FE" : "#374151",
                              }}
                            >
                              {m.role}
                            </span>
                          </td>
                          <td>
                            {m.role !== "OWNER" && (
                              <button
                                className="link-btn link-btn--danger"
                                onClick={() => {
                                  setShowChannelMembers(false);
                                  handleRemoveChannelMember(m.id);
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="td-chm-footer">
                <button
                  className="btn btn--secondary"
                  onClick={() => setShowChannelMembers(false)}
                >
                  Close
                </button>
                {channelMembers.length > 0 && (
                  <button
                    className="btn btn--primary"
                    onClick={() => {
                      setShowAddChannelMember(true);
                      setShowChannelMembers(false);
                    }}
                  >
                    <Icon name="plus" size={14} /> Add Member
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showAddChannelMember && selectedChannel && (
          <FormDrawer
            open={showAddChannelMember}
            title={`Add Member to #${selectedChannel.name}`}
            subtitle="Select a team member to add to this channel"
            fields={addChannelMemberFields}
            onClose={() => setShowAddChannelMember(false)}
            submitLabel="Add to Channel"
            onSubmit={handleAddChannelMemberSubmit}
          />
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

export default TeamDetails;
